import { SxProps, Theme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { getRelativeFontSize, mediumFont, regularFont } from "utils/styles";

const BRAND = "#f8481c";
const BRAND_LIGHT = "#ff6b4a";

const blogEditPageStyles: Record<string, SxProps<Theme>> = {
  root: {
    minHeight: "100vh",
    position: "relative",
    overflow: "hidden",
    backgroundColor: (t) =>
      t.palette.mode === "dark"
        ? t.palette.background.default
        : alpha("#eef0f5", 1),
    py: { xs: 2, md: 4 },
    px: { xs: 2, sm: 3 },
  },
  backgroundLayer: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    overflow: "hidden",
    pointerEvents: "none",
    zIndex: 0,
  },
  gradientOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: (t) =>
      t.palette.mode === "dark"
        ? `radial-gradient(ellipse 80% 50% at 50% 0%, ${alpha(BRAND, 0.18)} 0%, transparent 50%), radial-gradient(ellipse 60% 40% at 80% 80%, ${alpha(BRAND, 0.12)} 0%, transparent 50%)`
        : `radial-gradient(ellipse 80% 50% at 50% 0%, ${alpha(BRAND, 0.12)} 0%, transparent 50%), radial-gradient(ellipse 60% 40% at 80% 80%, ${alpha(BRAND, 0.08)} 0%, transparent 50%)`,
    zIndex: 0,
  },
  contentWrapper: {
    position: "relative",
    zIndex: 1,
  },
  backButton: {
    textTransform: "none",
    fontWeight: 600,
    color: (t) => t.palette.text.secondary,
    "&:hover": {
      bgcolor: (t) => alpha(t.palette.text.secondary, 0.08),
      color: (t) => t.palette.text.primary,
    },
  },
  /** Back button + Edit Blog title on one line, below navbar */
  headerRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: 2,
    pt: { xs: 3, sm: 4 },
    pb: 2,
    position: "relative",
    zIndex: 1,
  },
  headerTitleBlock: {
    textAlign: { xs: "left", sm: "center" },
    flex: "1 1 auto",
    minWidth: 0,
  },
  headerBox: {
    position: "relative",
    zIndex: 1,
    textAlign: "center",
    py: { xs: 2, md: 3 },
  },
  headerText: (theme) => ({
    ...mediumFont,
    fontSize: getRelativeFontSize(28),
    color: theme.palette.text.primary,
    lineHeight: 1.2,
    position: "relative",
    display: "inline-block",
    "&::after": {
      content: '""',
      position: "absolute",
      bottom: -12,
      left: "50%",
      transform: "translateX(-50%)",
      width: 64,
      height: 4,
      borderRadius: 2,
      background: `linear-gradient(90deg, ${BRAND}, ${BRAND_LIGHT})`,
    },
    [theme.breakpoints.down("md")]: {
      fontSize: getRelativeFontSize(22),
    },
  }),
  subHeaderText: {
    ...regularFont,
    fontSize: getRelativeFontSize(2),
    color: (t) => t.palette.text.secondary,
    mt: 3,
    maxWidth: 560,
    mx: "auto",
    lineHeight: 1.6,
  },
  twoColGrid: {
    position: "relative",
    zIndex: 1,
    mt: 2,
    mb: 4,
  },
  /** 2x2 grid: each row’s two cards share the same height (row 1 pair, row 2 pair) */
  sectionsGrid: {
    display: "grid",
    gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
    gridTemplateRows: { xs: "auto auto auto auto", md: "auto auto" },
    gap: 3,
    width: "100%",
    alignContent: "start",
    "& > *": {
      minHeight: 0,
    },
  },
  card: {
    p: { xs: 2, md: 3 },
    borderRadius: "16px",
    border: (t) => `1px solid ${alpha(t.palette.divider, 0.12)}`,
    bgcolor: (t) => t.palette.background.paper,
    height: "100%",
    display: "flex",
    flexDirection: "column",
    boxShadow: (t) =>
      t.palette.mode === "dark"
        ? `0 8px 32px ${alpha("#000", 0.25)}`
        : `0 8px 32px ${alpha("#000", 0.08)}`,
  },
  formCardBody: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
  },
  previewCard: {
    p: { xs: 2, md: 3 },
    borderRadius: "16px",
    border: (t) => `1px solid ${alpha(t.palette.divider, 0.12)}`,
    bgcolor: (t) => t.palette.background.paper,
    height: "100%",
    display: "flex",
    flexDirection: "column",
    minHeight: 520,
    boxShadow: (t) =>
      t.palette.mode === "dark"
        ? `0 8px 32px ${alpha("#000", 0.25)}`
        : `0 8px 32px ${alpha("#000", 0.08)}`,
  },
  previewScroll: {
    flex: 1,
    overflowY: "auto",
    minHeight: 0,
  },
  sectionTitle: {
    fontSize: "1.125rem",
    fontWeight: 700,
    color: (t) => t.palette.text.primary,
    mb: 2,
    pb: 1,
    borderBottom: (t) => `2px solid ${alpha(BRAND, 0.2)}`,
  },
  fieldLabel: {
    fontSize: "0.875rem",
    fontWeight: 600,
    color: (t) => t.palette.text.secondary,
    mb: 1,
    display: "block",
  },
  textField: {
    "& .MuiOutlinedInput-root": {
      borderRadius: "12px",
      bgcolor: (t) =>
        t.palette.mode === "dark"
          ? alpha(t.palette.background.default, 0.5)
          : alpha(t.palette.background.paper, 0.9),
      "& fieldset": {
        borderWidth: 1.5,
        borderColor: (t) =>
          t.palette.mode === "dark"
            ? alpha(t.palette.divider, 0.6)
            : alpha(t.palette.common.black, 0.22),
      },
      "&:hover fieldset": {
        borderColor: (t) => alpha(BRAND, 0.6),
      },
      "&.Mui-focused fieldset": {
        borderColor: BRAND,
        borderWidth: 2,
        boxShadow: (t) => `0 0 0 4px ${alpha(BRAND, 0.15)}`,
      },
    },
  },
  /** Shared card style for each section; fill grid cell and allow inner scroll */
  sectionCard: {
    p: { xs: 2, md: 3 },
    borderRadius: "16px",
    border: (t) => `1px solid ${alpha(t.palette.divider, 0.12)}`,
    bgcolor: (t) => t.palette.background.paper,
    display: "flex",
    flexDirection: "column",
    boxShadow: (t) =>
      t.palette.mode === "dark"
        ? `0 8px 32px ${alpha("#000", 0.25)}`
        : `0 8px 32px ${alpha("#000", 0.08)}`,
    height: "100%",
    minHeight: 0,
    overflow: "hidden",
  },
  contentSectionForm: {
    p: { xs: 2, md: 3 },
    borderRadius: "16px",
    border: (t) => `1px solid ${alpha(t.palette.divider, 0.12)}`,
    bgcolor: (t) => t.palette.background.paper,
    display: "flex",
    flexDirection: "column",
    boxShadow: (t) =>
      t.palette.mode === "dark"
        ? `0 8px 32px ${alpha("#000", 0.25)}`
        : `0 8px 32px ${alpha("#000", 0.08)}`,
    height: "100%",
    minHeight: 0,
    overflow: "hidden",
  },
  contentSectionPreview: {
    p: { xs: 2, md: 3 },
    borderRadius: "16px",
    border: (t) => `1px solid ${alpha(t.palette.divider, 0.12)}`,
    bgcolor: (t) => t.palette.background.paper,
    display: "flex",
    flexDirection: "column",
    boxShadow: (t) =>
      t.palette.mode === "dark"
        ? `0 8px 32px ${alpha("#000", 0.25)}`
        : `0 8px 32px ${alpha("#000", 0.08)}`,
    height: "100%",
    minHeight: 0,
    overflow: "hidden",
  },
  /** Scrollable body inside content cards so they don't burst the grid cell */
  sectionCardScroll: {
    flex: 1,
    minHeight: 0,
    overflow: "auto",
  },
  dropzone: {
    borderRadius: "12px",
    border: (t) => `2px dashed ${alpha(t.palette.divider, 0.4)}`,
    bgcolor: (t) =>
      t.palette.mode === "dark"
        ? alpha(t.palette.background.default, 0.3)
        : alpha(t.palette.grey[50], 0.8),
    py: 4,
    px: 2,
    textAlign: "center",
    cursor: "pointer",
    transition: "all 0.2s ease",
    "&:hover": {
      borderColor: (t) => alpha(BRAND, 0.6),
      bgcolor: (t) => alpha(BRAND, 0.04),
    },
  },
  dropzoneActive: {
    borderColor: (t) => alpha(BRAND, 0.8),
    bgcolor: (t) => alpha(BRAND, 0.08),
  },
  imagePreview: {
    borderRadius: "12px",
    overflow: "hidden",
    border: (t) => `1px solid ${alpha(t.palette.divider, 0.2)}`,
    position: "relative",
    "& img": {
      width: "100%",
      maxHeight: 320,
      objectFit: "cover",
      display: "block",
    },
  },
  imageRemoveBtn: {
    position: "absolute",
    top: 12,
    right: 12,
    bgcolor: (t) => alpha(t.palette.common.black, 0.6),
    color: "#fff",
    "&:hover": {
      bgcolor: (t) => alpha(t.palette.common.black, 0.8),
    },
  },
  blogCardPreview: {
    overflow: "hidden",
    background: (t) =>
      t.palette.mode === "dark"
        ? `linear-gradient(135deg, ${alpha("#1b1b1b", 0.95)} 0%, ${alpha("#252525", 0.95)} 100%)`
        : `linear-gradient(135deg, ${alpha("#ffffff", 0.95)} 0%, ${alpha("#fafafa", 0.95)} 100%)`,
    border: (t) => `1px solid ${alpha(t.palette.divider, 0.1)}`,
    borderLeft: (t) => `3px solid ${alpha(BRAND, 0.3)}`,
    borderRadius: "12px",
    boxShadow: (t) => `0 2px 12px ${alpha("#000", 0.08)}`,
  },
  blogCardPreviewImageWrap: {
    position: "relative",
    width: "100%",
    height: 200,
    overflow: "hidden",
    bgcolor: (t) => alpha(BRAND, 0.05),
  },
  blogCardPreviewBody: {
    p: 2,
  },
  previewSectionLabel: {
    fontSize: "0.75rem",
    fontWeight: 700,
    color: (t) => t.palette.text.secondary,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    mb: 1.5,
  },
  primaryButton: {
    minHeight: 44,
    px: 3,
    borderRadius: "12px",
    fontWeight: 600,
    textTransform: "none",
    bgcolor: BRAND,
    color: "#fff",
    boxShadow: "none",
    "&:hover": {
      bgcolor: "#e03d18",
      boxShadow: "none",
    },
    "&:disabled": {
      bgcolor: (t) => alpha(BRAND, 0.5),
      color: "#fff",
    },
  },
  rejectButton: {
    minHeight: 44,
    px: 3,
    borderRadius: "12px",
    fontWeight: 600,
    textTransform: "none",
    border: (t) => `2px solid ${t.palette.error.main}`,
    color: (t) => t.palette.error.main,
    bgcolor: "transparent",
    "&:hover": {
      borderColor: (t) => t.palette.error.dark,
      color: (t) => t.palette.error.dark,
      bgcolor: (t) => alpha(t.palette.error.main, t.palette.mode === "dark" ? 0.2 : 0.08),
    },
    "&:disabled": {
      borderColor: (t) => alpha(t.palette.error.main, 0.5),
      color: (t) => alpha(t.palette.error.main, 0.5),
    },
  },
};

export default blogEditPageStyles;
